import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { BodyText } from 'components/UI/Brand/text';
import { useLocation } from 'react-router';
import { IGeneralSection } from 'interfaces/IMarketing';
import { useNavigate } from 'react-router-dom';
import TitleAndDescription from 'containers/Learn/TitleAndDescription';
import LayoutTypes from 'containers/Learn/Articles/LayoutTypes';
import LayoutA from 'containers/Learn/Articles/LayoutA';
import LayoutB from 'containers/Learn/Articles/LayoutB';
import LayoutC from 'containers/Learn/Articles/LayoutC';
import LayoutD from 'containers/Learn/Articles/LayoutD';
import LoadingGif from 'components/UI/Loading/LoadingGif';
import { executeScroll } from 'utils/executeScroll';
import AnchorNavigationHeader from 'components/Header/AnchorNavigationHeader/AnchorNavigationHeader';
import { dataLayerPush, getMarketingAnalyticsObject } from 'utils/analytics';
import { media } from 'style/sizes';
import ExpandableList from 'components/UI/ExpandableList/ExpandableList';
import { onContentHeader } from 'store/actions';
import { Document } from '@contentful/rich-text-types';
import { useAppDispatch, useAppSelector } from 'hooks/storeHooks';
import { AlertBox } from 'components';
import { useMediaQuery } from 'hooks/useMediaQuery';
import { HEADER_SIZE } from '../utils/getHeader.util';
import styles from './styles.module.scss';

interface ILayoutContainer {
  articles: IGeneralSection[];
  description?: string;
  descriptionAsNode?: Document;
  disclaimer?: string;
  isArticle?: boolean;
  isUsingAnchorLinks?: boolean;
  layout: string;
  title: string;
  loadingRequired?: boolean;
  index?: number;
  isArticleContainer?: boolean;
}

const LayoutContainer = ({
  articles,
  description,
  disclaimer,
  isArticle = false,
  isUsingAnchorLinks = false,
  layout,
  descriptionAsNode,
  title,
  loadingRequired = true,
  index,
  isArticleContainer = false,
}: ILayoutContainer) => {
  const isMobile = useMediaQuery(media.maxTablet);
  const navigate = useNavigate();

  const [initialVisibleItemsCount, setInitialVisibleItemsCount] = useState(
    isMobile ? 3 : 8
  );
  const [navLinks, setNavLinks] = useState([]);
  const [activeLink, setActiveLink] = useState<string>('');
  const [visibleItemsCount, setVisibleItemsCount] = useState<number>(
    initialVisibleItemsCount
  );

  const dispatch = useAppDispatch();
  const location = useLocation();

  const loading = useAppSelector((state) =>
    Object.values(state.api.queries).some(
      (query) => query && query.status === 'pending'
    )
  );

  useEffect(() => {
    const links = [];
    if (articles && isUsingAnchorLinks) {
      links.push(
        articles.map(({ title: sectionTitle }) => ({
          label: sectionTitle,
          onClickHandler: () => {
            setActiveLink(sectionTitle);
            executeScroll(sectionTitle, 100);
            navigate(`#${sectionTitle}`);
          },
        }))
      );
      dispatch(onContentHeader(true));
    }
    setNavLinks(links.flat());
  }, [articles]);

  useEffect(() => {
    if (navLinks.length > 0) {
      setActiveLink(navLinks[0].label);
    }
  }, [navLinks]);

  useEffect(() => {
    if (isUsingAnchorLinks) {
      dispatch(onContentHeader(true));
    }
  }, []);

  useEffect(() => {
    setInitialVisibleItemsCount(isMobile ? 3 : 8);
  }, [isMobile]);

  useEffect(() => {
    setVisibleItemsCount(initialVisibleItemsCount);
  }, [initialVisibleItemsCount]);

  const onDownloadClick = (label: string) => {
    const analyticsData = getMarketingAnalyticsObject(title, label);
    dataLayerPush('downloads', analyticsData);
  };
  useEffect(() => {
    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveLink(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.1,
    });

    articles.forEach(({ title: sectionTitle }) => {
      const target = document.getElementById(sectionTitle);
      if (target) {
        observer.observe(target);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [articles]);
  let InnerLayout;

  switch (layout) {
    case LayoutTypes.B: {
      InnerLayout = LayoutB;
      break;
    }
    case LayoutTypes.C: {
      InnerLayout = LayoutC;
      break;
    }
    case LayoutTypes.D: {
      InnerLayout = LayoutD;
      break;
    }
    case LayoutTypes.A:
    default: {
      InnerLayout = LayoutA;
      break;
    }
  }

  if (loadingRequired && loading) {
    return (
      <div className={styles.loader}>
        <LoadingGif />
      </div>
    );
  }

  return (
    <div
      className={clsx(styles.layoutsContainer, {
        [styles.isArticle]: isArticle,
        [styles.isStrictWidth]: isArticleContainer,
      })}
    >
      <TitleAndDescription
        title={index === 0 ? '' : title}
        description={description}
        descriptionAsNode={descriptionAsNode}
        isArticle={isArticle}
        isArticleContainer={isArticleContainer}
      />
      {isUsingAnchorLinks && (
        <div className={styles.anchorNavigationContainer}>
          <AnchorNavigationHeader items={navLinks} activeItem={activeLink} />
        </div>
      )}
      {location.pathname === '/marketing' ? (
        <ExpandableList
          visibleItemsCount={visibleItemsCount}
          setVisibleItemsCount={setVisibleItemsCount}
          initialVisibleItemsCount={initialVisibleItemsCount}
          childrenLength={articles.length}
        >
          <InnerLayout
            articles={articles.slice(0, visibleItemsCount)}
            isArticle={isArticle}
            onDownloadClick={onDownloadClick}
            visibleItemsCount={visibleItemsCount}
            highestHeadingSize={HEADER_SIZE.H2}
          />
        </ExpandableList>
      ) : (
        <InnerLayout
          articles={articles}
          isArticle={isArticle}
          onDownloadClick={onDownloadClick}
          highestHeadingSize={HEADER_SIZE.H2}
        />
      )}
      {disclaimer && (
        <AlertBox type="warning" className={styles.disclaimerContainer}>
          <BodyText dangerouslySetInnerHTML={{ __html: disclaimer }} />
        </AlertBox>
      )}
    </div>
  );
};

export default LayoutContainer;
